import { inject, Injectable } from "@angular/core";
import { mutationOptions, QueryClient, queryOptions } from "@tanstack/angular-query-experimental";
import _tap from "lodash/fp/tap";

import {
	CollectionBundle,
	DataBundle,
	FileDownloadBundle,
	PagedCollectionBundle,
	PagedCollectionData,
} from "@intocare/datatypes/lib/bundle";

import { SharedCommunicationDataApi } from "./communication-data.api";
import { CommunicationGroupsSearchParams, CommunicationGroupSummary } from "./communication-groups";
import {
	CommunicationMessage,
	CommunicationState,
	MessagesSearchParams,
	MessageSummary,
	RecipientsSearchParams,
	SentMessageRecipient,
	SentMessageSummary,
} from "./communication-messages";

const QUERY_KEYS = {
	base() {
		return ["communication"];
	},
	baseMessages() {
		return [...this.base(), "messages"];
	},
	messagesState() {
		return [...this.baseMessages(), "messages-state"];
	},
	baseMessageLists() {
		return [...this.baseMessages(), "lists"];
	},
	baseMessagesReceived() {
		return [...this.baseMessageLists(), "received"];
	},
	messagesReceived(params: { siteId?: string; search: MessagesSearchParams }) {
		return [...this.baseMessagesReceived(), params.siteId, params.search];
	},
	messagesSent(params: { search: MessagesSearchParams }) {
		return [...this.baseMessageLists(), "sent", params.search];
	},
	baseMessageDetails() {
		return [...this.baseMessages(), "details"];
	},
	messageReceivedDetails(params: { messageId: string }) {
		return [...this.baseMessageDetails(), "received", params.messageId];
	},
	messageSentDetails(params: { messageId: string }) {
		return [...this.baseMessageDetails(), "sent-details", params.messageId];
	},
	messageSentRecipients(params: { messageId: string; search: RecipientsSearchParams }) {
		return [...this.baseMessageDetails(), "sent-recipients", params.messageId, params.search];
	},
	baseCommunicationGroups() {
		return [...this.base(), "groups"];
	},
	baseCommunicationGroupLists() {
		return [...this.baseCommunicationGroups(), "lists"];
	},
	communicationGroups(params: { search: CommunicationGroupsSearchParams }) {
		return [...this.baseCommunicationGroupLists(), params.search];
	},
};

@Injectable({ providedIn: "root" })
export class SharedCommunicationDataService {
	private readonly api = inject(SharedCommunicationDataApi);
	private readonly queryClient = inject(QueryClient);

	invalidateMessages() {
		void this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.baseMessages() });
	}

	messagesStateQueryOptions() {
		return queryOptions<DataBundle<CommunicationState>>({
			queryKey: QUERY_KEYS.messagesState(),
			queryFn: () => this.api.getMessagesState(),
			// We will handle invalidation manually + update data from retrieving messages.
			refetchOnMount: false,
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
			throwOnError: false,
		});
	}

	invalidateMessagesState() {
		void this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.messagesState() });
	}

	/**
	 * Update the query data of the message state.
	 *
	 * Instead of invalidating each time, some calls will add this state in their bundle, and thus we can use that to
	 * update the cached data to avoid the extra invalidation.
	 */
	private updateMessagesState<T extends DataBundle<CommunicationState>>(bundle: T) {
		this.queryClient.setQueryData(QUERY_KEYS.messagesState(), { data: { messages: bundle.data.messages } });
	}

	receivedMessagesQueryOptions(params: { siteId?: string; search: MessagesSearchParams }) {
		return queryOptions<DataBundle<PagedCollectionData<DataBundle<MessageSummary>> & CommunicationState>>({
			queryKey: QUERY_KEYS.messagesReceived(params),
			queryFn: () =>
				this.api.getMessages(params).then(
					// Type is needed here to avoid TS error, as we get error on `PromiseLike` !?
					_tap((bundle: DataBundle<PagedCollectionData<DataBundle<MessageSummary>> & CommunicationState>) =>
						this.updateMessagesState(bundle),
					),
				),
			throwOnError: false,
			gcTime: 0, // Ensures the user always sees the latest state of the messages.
		});
	}

	/**
	 * Invalidates data and state related to the read state of messages.
	 *
	 * Functionally, when we open a message detail, we set it as read if that wasn't done yet. This means that we need to
	 * invalidate counters and the list item to update accordingly. When we are certain the message was already read, then
	 * invalidation can be skipped. A future improvement could be that BE returns this information as bundle metadata.
	 */
	private invalidateMessageReadStatus<T extends { data: { id: string } }>(bundle: T) {
		if (
			!this.queryClient
				.getQueriesData<CollectionBundle<DataBundle<MessageSummary>>>({ queryKey: QUERY_KEYS.baseMessagesReceived() })
				.some(([_, cachedBundle]) =>
					cachedBundle?.data.items.some((item) => {
						return item.data.id === bundle.data.id && item.data.readOn !== undefined;
					}),
				)
		) {
			void this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.messagesState() });
			void this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.baseMessagesReceived() });
		}
	}

	receivedMessageQueryOptions(params: { messageId: string }) {
		return queryOptions<DataBundle<CommunicationMessage>>({
			queryKey: QUERY_KEYS.messageReceivedDetails(params),
			queryFn: () =>
				this.api.readMessageById(params).then(
					// Type is needed here to avoid TS error, as we get error on `PromiseLike` !?
					_tap((bundle: DataBundle<CommunicationMessage>) => this.invalidateMessageReadStatus(bundle)),
				),
		});
	}

	sentMessagesQueryOptions(params: { search: MessagesSearchParams }) {
		return queryOptions<PagedCollectionBundle<DataBundle<SentMessageSummary>>>({
			queryKey: QUERY_KEYS.messagesSent(params),
			queryFn: () => this.api.getSentMessages(params),
			throwOnError: false,
			gcTime: 0, // Ensures the user always sees the latest state of the messages.
		});
	}

	messageAttachmentUrlMutationOptions() {
		return mutationOptions({
			mutationFn: (params: { messageId: string; uploadId: string }): Promise<FileDownloadBundle> =>
				this.api.getMessageAttachmentUrl(params),
		});
	}

	sentMessageQueryOptions(params: { messageId: string }) {
		return queryOptions<DataBundle<CommunicationMessage>>({
			queryKey: QUERY_KEYS.messageSentDetails(params),
			queryFn: () => this.api.getSentMessageById(params),
			throwOnError: false,
		});
	}

	sentMessageRecipientsQueryOptions(params: { messageId: string; search: RecipientsSearchParams }) {
		return queryOptions<PagedCollectionBundle<DataBundle<SentMessageRecipient>>>({
			queryKey: QUERY_KEYS.messageSentRecipients(params),
			queryFn: () => this.api.getSentMessageRecipientsById(params),
			throwOnError: false,
			gcTime: 0, // Recipients can read messages sporadically, setting this to 0 ensures we always fetch and display only the latest data, instead of cached data.
		});
	}

	communicationGroupsQueryOptions(
		params: { search: CommunicationGroupsSearchParams },
		options?: { staleTime?: number },
	) {
		return queryOptions<PagedCollectionBundle<DataBundle<CommunicationGroupSummary>>>({
			queryKey: QUERY_KEYS.communicationGroups(params),
			queryFn: () => this.api.getCommunicationGroups(params),
			throwOnError: false,
			...options,
		});
	}
}
